import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import Pagination from "../../components/ui/Pagination";
import EmployeePicker from "../../components/ui/EmployeePicker";
import {
  useAuditLog,
  useAuditLogs,
  useAuditModules,
} from "../../api/queries/adminQueries";
import useCurrentUser from "../../hooks/useCurrentUser";
import { getApiErrorMessage } from "../../utils/apiError";
import { formatDateTime, fromNow } from "../../utils/dateTime";
import { employeeName } from "../../utils/text";
import "../attendance/attendance.css";
import "./AuditLogsPage.css";
import usePermissions from "../../hooks/usePermissions";

const EMPTY = [];
const ACTIONS = ["CREATE", "UPDATE", "DELETE"];
const ACTION_TONE = { CREATE: "green", UPDATE: "blue", DELETE: "red" };
const FILTER_KEYS = ["module", "action", "from", "to", "search", "employeeId"];

/** Values shown in the diff: objects as JSON, empty as "—". */
const show = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

/** Old → new for every field in either side; changed fields first. */
const Diff = ({ oldValues, newValues }) => {
  const o = oldValues || {};
  const n = newValues || {};
  const keys = [...new Set([...Object.keys(o), ...Object.keys(n)])];
  const rows = keys
    .map((k) => ({
      k,
      before: show(o[k]),
      after: show(n[k]),
      changed: JSON.stringify(o[k]) !== JSON.stringify(n[k]),
    }))
    .sort(
      (a, b) => Number(b.changed) - Number(a.changed) || a.k.localeCompare(b.k),
    );
  if (!rows.length)
    return (
      <p className="fs-085 text-slate-500 mb-0">No field values recorded.</p>
    );
  return (
    <div className="table-responsive">
      <table className="table table-sm mb-0 audit-diff">
        <thead>
          <tr>
            <th>Field</th>
            <th>Old</th>
            <th>New</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.k} className={r.changed ? "is-changed" : ""}>
              <td className="fw-semibold">{r.k}</td>
              <td className="audit-old">{r.before}</td>
              <td className="audit-new">{r.after}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AuditDrawer = ({ id, onClose }) => {
  const query = useAuditLog(id);
  const log = query.data;
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const e = log?.employee;
  return (
    <div className="app-drawer-backdrop" onClick={onClose}>
      <aside
        className="app-drawer d-flex flex-column"
        role="dialog"
        aria-modal="true"
        aria-labelledby="audit-title"
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="app-dialog-header">
          <div>
            <h5 id="audit-title" className="fw-bold text-navy m-0">
              {log ? `${log.module} · ${log.entityName}` : "Change"}
            </h5>
            {log && (
              <div className="d-flex flex-wrap gap-2 align-items-center mt-1 fs-080 text-slate-500">
                <span
                  className={`status-pill status-pill--${ACTION_TONE[log.action] || "gray"}`}
                >
                  {log.action}
                </span>
                <span>
                  {log.updatedBy?.name || "System"} ·{" "}
                  {formatDateTime(log.updatedAt)}
                </span>
              </div>
            )}
          </div>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={onClose}
          ></button>
        </div>
        <div className="app-drawer-body">
          {query.isLoading && (
            <div className="text-center py-5">
              <span
                className="spinner-border text-primary"
                role="status"
                aria-label="Loading"
              ></span>
            </div>
          )}
          {query.isError && (
            <div className="alert alert-danger fs-085">
              {getApiErrorMessage(query.error, "Could not load this change.")}
            </div>
          )}
          {log && (
            <div className="d-flex flex-column gap-3">
              {e && (
                <section className="audit-employee">
                  <div className="approval-meta-label">Employee</div>
                  <div className="fw-bold text-navy">{e.name}</div>
                  <div className="fs-080 text-slate-600">
                    {e.employeeNumber}
                    {e.email ? ` · ${e.email}` : ""}
                  </div>
                  <div className="audit-employee-grid">
                    <div>
                      <span className="approval-meta-label">Status</span>
                      {e.status || "—"}
                    </div>
                    <div>
                      <span className="approval-meta-label">Branch</span>
                      {e.branchName || "—"}
                    </div>
                    <div>
                      <span className="approval-meta-label">Department</span>
                      {e.departmentName || "—"}
                    </div>
                    <div>
                      <span className="approval-meta-label">Job title</span>
                      {e.jobTitle || "—"}
                    </div>
                  </div>
                </section>
              )}
              <div className="fs-080 text-slate-600">
                Record: <span className="font-monospace">{log.entityName}</span>
                {log.entityId ? (
                  <>
                    {" "}
                    · <span className="font-monospace">{log.entityId}</span>
                  </>
                ) : null}
              </div>
              {log.reason && <div className="approval-note">{log.reason}</div>}
              <Diff oldValues={log.oldValues} newValues={log.newValues} />
            </div>
          )}
        </div>
      </aside>
    </div>
  );
};

/** Admins/HR: every change in their company tree, filterable by module. */
const AuditLogsPage = () => {
  const user = useCurrentUser();
  // HR-level (audit-logs with COMPANY scope); role names grant nothing
  const isCompanyWide = usePermissions().companyWide("audit-logs");
  const [params, setParams] = useSearchParams();
  const page = Number(params.get("page")) || 1;
  const limit = Number(params.get("limit")) || 20;
  const filters = useMemo(
    () => Object.fromEntries(FILTER_KEYS.map((k) => [k, params.get(k) || ""])),
    [params],
  );
  const [employee, setEmployee] = useState(null);
  const [openId, setOpenId] = useState(null);

  const update = (changes, { resetPage = true } = {}) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) =>
      v ? next.set(k, v) : next.delete(k),
    );
    if (resetPage && !("page" in changes)) next.delete("page");
    setParams(next, { replace: true });
  };

  const [searchText, setSearchText] = useState(filters.search);
  useEffect(() => {
    const value = searchText.trim();
    if (value === filters.search) return undefined;
    const t = setTimeout(() => update({ search: value }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const modules = useAuditModules();
  const query = useAuditLogs({ ...filters, page, limit });
  const rows = query.data?.data || EMPTY;
  const hasFilters = FILTER_KEYS.some((k) => filters[k]);

  if (!isCompanyWide) {
    return (
      <Layout>
        <div className="container-fluid py-4 px-lg-4">
          <div className="att-card approval-empty">
            <i className="bi bi-shield-lock"></i>Only Admin and HR can view the
            audit log.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="mb-4">
          <h1 className="fw-bold text-dark m-0 page-title">Audit Log</h1>
          <p className="text-muted small mb-0">
            Every change, who made it and when. Click a row for the details.
          </p>
        </div>

        <div className="att-filters mb-3">
          <div className="position-relative att-filter-search">
            <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-slate-500 fs-080"></i>
            <input
              type="search"
              className="filter-input w-100 ps-5"
              placeholder="Search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              aria-label="Search"
            />
          </div>
          <select
            className="status-select"
            value={filters.module}
            onChange={(e) => update({ module: e.target.value })}
            aria-label="Module"
          >
            <option value="">All modules</option>
            {(modules.data || EMPTY).map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          <select
            className="status-select"
            value={filters.action}
            onChange={(e) => update({ action: e.target.value })}
            aria-label="Action"
          >
            <option value="">All actions</option>
            {ACTIONS.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
          <div className="d-flex align-items-center gap-1">
            <input
              type="date"
              className="filter-input"
              value={filters.from}
              max={filters.to || undefined}
              onChange={(e) => update({ from: e.target.value })}
              aria-label="From"
            />
            <span className="text-slate-400">–</span>
            <input
              type="date"
              className="filter-input"
              value={filters.to}
              min={filters.from || undefined}
              onChange={(e) => update({ to: e.target.value })}
              aria-label="To"
            />
          </div>
          <div className="audit-employee-filter">
            <EmployeePicker
              companyId={user.companyId}
              value={employee}
              onChange={(emp) => {
                setEmployee(emp);
                update({ employeeId: emp?.id || "" });
              }}
              placeholder="Filter by employee"
            />
          </div>
          {hasFilters && (
            <button
              type="button"
              className="btn btn-sm btn-link text-slate-600"
              onClick={() => {
                setSearchText("");
                setEmployee(null);
                update(Object.fromEntries(FILTER_KEYS.map((k) => [k, ""])));
              }}
            >
              Clear
            </button>
          )}
        </div>

        <div className="glass-table-card">
          {query.isError ? (
            <div className="approval-empty text-danger">
              <i className="bi bi-exclamation-triangle"></i>
              {getApiErrorMessage(query.error, "Could not load the audit log.")}
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover mb-0 att-table">
                  <thead className="custom-table-header">
                    <tr>
                      <th className="ps-4 py-3">Updated by</th>
                      <th className="py-3">Update date</th>
                      <th className="py-3">Module</th>
                      <th className="py-3">Record</th>
                      <th className="py-3">Action</th>
                      <th className="py-3 pe-4">Employee</th>
                    </tr>
                  </thead>
                  <tbody className="border-top-0">
                    {rows.map((r) => (
                      <tr
                        key={r.id}
                        className="cursor-pointer"
                        onClick={() => setOpenId(r.id)}
                      >
                        <td className="ps-4 fw-semibold">
                          {r.updatedBy?.name || "System"}
                        </td>
                        <td
                          className="text-nowrap"
                          title={fromNow(r.updatedAt)}
                        >
                          {formatDateTime(r.updatedAt)}
                        </td>
                        <td>{r.module}</td>
                        <td>
                          <div>{r.entityName}</div>
                          {r.entityId && (
                            <div className="fs-070 text-slate-400 font-monospace">
                              {String(r.entityId).slice(0, 8)}
                            </div>
                          )}
                        </td>
                        <td>
                          <span
                            className={`status-pill status-pill--${ACTION_TONE[r.action] || "gray"}`}
                          >
                            {r.action}
                          </span>
                        </td>
                        <td className="pe-4">
                          {r.employee ? (
                            <>
                              <div className="emp-name-text">
                                {r.employee.name || employeeName(r.employee)}
                              </div>
                              <div className="emp-sub-text">
                                {r.employee.employeeNumber}
                              </div>
                            </>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {!query.isLoading && rows.length === 0 && (
                      <tr>
                        <td colSpan={6} className="approval-empty">
                          <i className="bi bi-journal-text"></i>No changes
                          match.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {query.isLoading && (
                <div className="text-center py-5">
                  <span
                    className="spinner-border text-primary"
                    role="status"
                    aria-label="Loading"
                  ></span>
                </div>
              )}
              <Pagination
                page={page}
                limit={limit}
                total={query.data?.total || 0}
                onPageChange={(p) =>
                  update({ page: String(p) }, { resetPage: false })
                }
                onLimitChange={(l) => update({ limit: String(l) })}
              />
            </>
          )}
        </div>
      </div>
      {openId && <AuditDrawer id={openId} onClose={() => setOpenId(null)} />}
    </Layout>
  );
};

export default AuditLogsPage;
